import { Box, Text } from "@primer/react";
import { LocationIcon, MailIcon, GlobeIcon, DeviceMobileIcon } from "@primer/octicons-react";
import { ReactNode } from "react";
import { appConst } from "../AppConst";
import { useIsMobile } from "../hooks/useIsMobile";
import { colors } from "../theme/tokens";

const ContactBlock = () => {
    const { logoUrl, webName } = appConst.branding;
    const { address, email, hotline, fax, website } = appConst.contact;

    return (
        <Box sx={{ display: "flex", gap: 3, flex: 1, flexWrap: "wrap" }}>
            {logoUrl && (
                <Box
                    as="img"
                    src={logoUrl}
                    alt={webName}
                    sx={{
                        height: 80,
                        bg: "#fff",
                        p: 2,
                        borderRadius: 2,
                        objectFit: "contain",
                        flexShrink: 0,
                    }}
                />
            )}
            <Box as="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                <Text as="li" sx={{ fontWeight: 700, fontSize: 2, mb: 1, color: colors.footerText }}>
                    {webName}
                </Text>
                {address && (
                    <Box as="li" sx={{ display: "flex", alignItems: "baseline", gap: 2, fontSize: 1, color: colors.footerText }}>
                        <LocationIcon size={14} /> {address}
                    </Box>
                )}
                {email && (
                    <Box as="li" sx={{ display: "flex", alignItems: "baseline", gap: 2, fontSize: 1, color: colors.footerText }}>
                        <MailIcon size={14} /> {email}
                    </Box>
                )}
                {(hotline || fax) && (
                    <Box as="li" sx={{ display: "flex", alignItems: "baseline", gap: 2, fontSize: 1, color: colors.footerText }}>
                        <DeviceMobileIcon size={14} /> ĐT: {hotline}{fax ? ` · Fax: ${fax}` : ""}
                    </Box>
                )}
                {website && (
                    <Box as="li" sx={{ display: "flex", alignItems: "baseline", gap: 2, fontSize: 1, color: colors.footerText }}>
                        <GlobeIcon size={14} />
                        <Box
                            as="a"
                            href={website}
                            target="_blank"
                            rel="noopener"
                            sx={{ color: colors.footerText, textDecoration: "underline" }}
                        >
                            {website}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const SupportBlock = () => {
    const { daoTao, tech } = appConst.support;

    return (
        <Box
            sx={{
                bg: "#f9f9f9",
                color: "#333",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                flex: 1,
                minWidth: 320,
            }}
        >
            {daoTao && (daoTao.email || daoTao.phone) && (
                <Box sx={{ flex: 1, minWidth: 200, borderRight: ["none", "none", "1px solid #ccc"], pr: [0, 0, 3] }}>
                    <Text sx={{ display: "block", fontSize: 2, fontWeight: 700, color: "#333", mb: 1 }}>
                        Hỗ trợ về học phần
                    </Text>
                    <Text sx={{ display: "block", fontSize: 1, color: "#555", mb: 2 }}>
                        Sinh viên gặp vướng mắc khi đăng ký học phần liên hệ:
                    </Text>
                    <Text sx={{ display: "block", fontSize: 1, color: "#333" }}>
                        <strong>{daoTao.name}:</strong>
                        <br />
                        {daoTao.email && (
                            <Box as="a" href={`mailto:${daoTao.email}`} sx={{ color: "#0969da", fontWeight: 600, mr: 2 }}>
                                {daoTao.email}
                            </Box>
                        )}
                        {daoTao.phone && (
                            <Box as="a" href={`tel:${daoTao.phone.replace(/\./g, "")}`} sx={{ color: "#0969da", fontWeight: 600 }}>
                                {daoTao.phone}
                            </Box>
                        )}
                    </Text>
                </Box>
            )}
            {tech.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Text sx={{ display: "block", fontSize: 2, fontWeight: 700, color: "#333", mb: 1 }}>
                        Hỗ trợ kỹ thuật
                    </Text>
                    <Text sx={{ display: "block", fontSize: 1, color: "#555", mb: 2 }}>
                        Liên hệ trực tiếp các đơn vị chức năng:
                    </Text>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {tech.map(t => (
                            <Box key={t.name} sx={{ minWidth: 100 }}>
                                <Text sx={{ display: "block", fontSize: 1, color: "#333" }}>
                                    <strong>{t.name}:</strong>
                                </Text>
                                <Box
                                    as="a"
                                    href={`tel:${t.phone.replace(/\./g, "")}`}
                                    sx={{ color: "#0969da", fontWeight: 600, fontSize: 1 }}
                                >
                                    {t.phone}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

/* ============================ Bản mobile ============================ */

/** 1 dòng liên hệ — icon căn giữa với text. Chữ nhỏ (fontSize 0) cho gọn. */
const MContactRow = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, fontSize: 0, color: "#dfe3e8" }}>
        <Box sx={{ color: "#9aa1ab", flexShrink: 0, display: "flex", alignItems: "center" }}>
            {icon}
        </Box>
        <Box sx={{ minWidth: 0, wordBreak: "break-word" }}>{children}</Box>
    </Box>
);

/** Chip liên hệ bấm gọi/gửi mail — touch target ≥ 44px. */
const MChipLink = ({ href, icon, label }: { href: string; icon: ReactNode; label: string }) => (
    <Box
        as="a"
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noopener"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            minHeight: 44,
            px: "10px",
            bg: "rgba(255,255,255,0.1)",
            borderRadius: "8px",
            color: "#cfe3ff",
            fontWeight: 600,
            fontSize: 0,
            textDecoration: "none",
        }}
    >
        {icon} {label}
    </Box>
);

/**
 * Footer bản mobile — gọn: gộp thương hiệu + liên hệ chung 1 khối, padding nhỏ,
 * chữ nhỏ, chip hỗ trợ xếp ngang (wrap). Nền tối liền mạch, chip bấm gọi được (≥44px).
 */
const MobileFooter = () => {
    const { logoUrl, webName, copyright } = appConst.branding;
    const { address, email, hotline, fax, website } = appConst.contact;
    const { daoTao, tech } = appConst.support;

    const headingSx = {
        display: "block",
        fontWeight: 700,
        fontSize: 0,
        letterSpacing: "0.04em",
        textTransform: "uppercase" as const,
        color: "#9aa1ab",
        mb: 2,
    };
    const hasSupport = (daoTao && (daoTao.email || daoTao.phone)) || tech.length > 0;

    return (
        <Box as="footer">
            <Box sx={{ bg: "#2f3640", color: "#dfe3e8" }}>
                {/* Thương hiệu + liên hệ — gộp 1 khối */}
                <Box sx={{ px: 3, py: "12px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        {logoUrl && (
                            <Box
                                as="img"
                                src={logoUrl}
                                alt={webName}
                                sx={{
                                    height: 32,
                                    width: 32,
                                    p: "3px",
                                    bg: "#fff",
                                    borderRadius: "6px",
                                    objectFit: "contain",
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Text sx={{ fontWeight: 700, fontSize: 1, lineHeight: 1.3 }}>{webName}</Text>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                        {address && (
                            <MContactRow icon={<LocationIcon size={13} />}>{address}</MContactRow>
                        )}
                        {hotline && (
                            <MContactRow icon={<DeviceMobileIcon size={13} />}>
                                <Box as="a" href={`tel:${hotline.replace(/\./g, "")}`} sx={{ color: "#cfe3ff", textDecoration: "none", fontWeight: 600 }}>
                                    {hotline}
                                </Box>
                                {fax && <Text sx={{ color: "#9aa1ab", ml: 2 }}>Fax: {fax}</Text>}
                            </MContactRow>
                        )}
                        {email && (
                            <MContactRow icon={<MailIcon size={13} />}>
                                <Box as="a" href={`mailto:${email}`} sx={{ color: "#cfe3ff", textDecoration: "none" }}>
                                    {email}
                                </Box>
                            </MContactRow>
                        )}
                        {website && (
                            <MContactRow icon={<GlobeIcon size={13} />}>
                                <Box as="a" href={website} target="_blank" rel="noopener" sx={{ color: "#cfe3ff", textDecoration: "none" }}>
                                    {website}
                                </Box>
                            </MContactRow>
                        )}
                    </Box>
                </Box>

                {/* Hỗ trợ — chip xếp ngang cho gọn */}
                {hasSupport && (
                    <Box sx={{ px: 3, py: "12px", borderTop: "1px solid rgba(255,255,255,0.09)" }}>
                        <Text sx={headingSx}>Cần hỗ trợ?</Text>

                        {daoTao && (daoTao.email || daoTao.phone) && (
                            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px", mb: tech.length > 0 ? 2 : 0 }}>
                                <Text sx={{ fontSize: 0, fontWeight: 600, mr: 1 }}>{daoTao.name}</Text>
                                {daoTao.email && (
                                    <MChipLink href={`mailto:${daoTao.email}`} icon={<MailIcon size={13} />} label={daoTao.email} />
                                )}
                                {daoTao.phone && (
                                    <MChipLink href={`tel:${daoTao.phone.replace(/\./g, "")}`} icon={<DeviceMobileIcon size={13} />} label={daoTao.phone} />
                                )}
                            </Box>
                        )}

                        {tech.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                {tech.map(t =>
                                    t.phone ? (
                                        <MChipLink
                                            key={t.name}
                                            href={`tel:${t.phone.replace(/\./g, "")}`}
                                            icon={<DeviceMobileIcon size={13} />}
                                            label={`${t.name}: ${t.phone}`}
                                        />
                                    ) : null
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Copyright */}
            <Box
                sx={{
                    bg: "#252a30",
                    color: "#9aa1ab",
                    textAlign: "center",
                    py: "10px",
                    px: 3,
                    fontSize: 0,
                    lineHeight: 1.5,
                    pb: "calc(10px + env(safe-area-inset-bottom))",
                }}
            >
                {copyright}
            </Box>
        </Box>
    );
};

const Footer = () => {
    const { copyright } = appConst.branding;
    const isMobile = useIsMobile();

    if (isMobile) {
        return <MobileFooter />;
    }

    return (
        <Box as="footer" sx={{ mt: 4 }}>
            <Box
                sx={{
                    bg: colors.footerBg,
                    color: colors.footerText,
                    px: [3, 4],
                    py: 4,
                    display: "flex",
                    gap: 4,
                    flexDirection: ["column", "column", "row"],
                    justifyContent: "space-between",
                }}
            >
                <ContactBlock />
                <SupportBlock />
            </Box>
            <Box
                sx={{
                    bg: colors.copyrightBg,
                    color: colors.copyrightText,
                    py: 2,
                    px: 3,
                    textAlign: "center",
                    fontSize: 0,
                    fontStyle: "italic",
                }}
            >
                {copyright}
            </Box>
        </Box>
    );
};

export default Footer;
